import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { IoExitOutline } from 'react-icons/io5';
import { isInRnWebView, postToRN, listenWebviewMessages } from '../bridge/webviewBridge';

const ROOT_PATHS = ['/home'];

export default function BackHandler() {
  const location = useLocation();
  const navigate = useNavigate();
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const confirmOpenRef = useRef(false);

  useEffect(() => {
    confirmOpenRef.current = showExitConfirm;
  }, [showExitConfirm]);

  useEffect(() => {
    if (!isInRnWebView()) return;
    const isRoot = ROOT_PATHS.includes(location.pathname);
    postToRN('NAV_STATE', {
      path: location.pathname,
      isRoot,
      canGoBackInWeb: !isRoot,
      hasBlockingUI: false,
      needsConfirm: false,
    });
  }, [location.pathname]);

  useEffect(() => {
    if (!isInRnWebView()) return;
    const off = listenWebviewMessages((msg) => {
      if (msg.type === 'BACK_REQUEST') {
        if (confirmOpenRef.current) {
          setShowExitConfirm(false);
          return;
        }
        if (window.history.length > 1) {
          navigate(-1);
        }
        return;
      }
      if (msg.type === 'APP_EXIT_REQUEST') {
        if (confirmOpenRef.current) {
          setShowExitConfirm(false);
          postToRN('EXIT_APP');
          return;
        }
        setShowExitConfirm(true);
      }
    });
    return off;
  }, [navigate]);

  if (!showExitConfirm) return null;

  return (
    <Overlay onClick={() => setShowExitConfirm(false)}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <IconWrap>
          <IoExitOutline size={28} color="#4A6741" />
        </IconWrap>
        <Title>앱을 종료하시겠습니까?</Title>
        <Desc>조군산업 파트너 앱을 닫습니다.</Desc>
        <BtnRow>
          <CancelBtn onClick={() => setShowExitConfirm(false)}>취소</CancelBtn>
          <ExitBtn
            onClick={() => {
              setShowExitConfirm(false);
              postToRN('EXIT_APP');
            }}
          >
            종료
          </ExitBtn>
        </BtnRow>
      </Modal>
    </Overlay>
  );
}

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;
const popIn = keyframes`
  from { transform: scale(0.92); opacity: 0; }
  to { transform: scale(1); opacity: 1; }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  animation: ${fadeIn} 0.18s ease;
`;
const Modal = styled.div`
  background: #fff;
  border-radius: 20px;
  width: 100%;
  max-width: 320px;
  padding: 28px 24px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.18);
  animation: ${popIn} 0.22s ease;
`;
const IconWrap = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #EDF2EB;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
`;
const Title = styled.h3`
  font-size: 17px;
  font-weight: 600;
  color: #1E2A32;
  margin: 0 0 6px;
  text-align: center;
`;
const Desc = styled.p`
  font-size: 13px;
  color: #8C9AA4;
  margin: 0 0 22px;
  text-align: center;
`;
const BtnRow = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;
`;
const BaseBtn = styled.button`
  flex: 1;
  height: 46px;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  &:active { transform: scale(0.97); }
`;
const CancelBtn = styled(BaseBtn)`
  background: #F4F5F2;
  color: #1E2A32;
`;
const ExitBtn = styled(BaseBtn)`
  background: #4A6741;
  color: #fff;
`;
